import { BaseFilterButton } from 'components/base-table/filters';
import { Button, FormControl, FormLabel, HStack, Input, Stack, useBoolean, useDisclosure } from 'design-system';
import { usePageParams } from 'hooks/use-page-params';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TInputs = {
  dateFrom: string;
  dateTo: string;
};

const defaultValues: TInputs = {
  dateFrom: '',
  dateTo: '',
};

export const DateRangeFilterButton = () => {
  const [isActive, setIsActive] = useBoolean(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();

  // Update active filter button
  useEffect(() => {
    if (params.dateFrom || params.dateTo) setIsActive.on();
    else setIsActive.off();
  }, [params.dateFrom, params.dateTo]);

  const { control, handleSubmit, formState, reset, setValue } = useForm<TInputs>({
    defaultValues: { ...defaultValues },
  });

  // Update values
  const updateValues = () => {
    setValue('dateFrom', (params?.dateFrom as string) ?? '');
    setValue('dateTo', (params?.dateTo as string) ?? '');
  };

  useEffect(() => {
    updateValues();
  }, [params.dateFrom, params.dateTo]);

  const onSubmit = (data: TInputs) => {
    // setPageParams strips empty-string values (skipEmptyString), so '' clears the param
    // the same way `undefined` would — and it satisfies ParsedQuery's value type.
    params.dateFrom = data.dateFrom || '';
    params.dateTo = data.dateTo || '';

    setPageParams(params);
    onClose();
    reset(data);
  };

  const handleClose = () => {
    onClose();
    updateValues();
  };

  const handleDisableFilterButton = () => {
    const isDisabled = Object.keys(formState.dirtyFields).length ? false : true;
    return isDisabled;
  };

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={isActive}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name='dateFrom'
            render={({ field: { onChange, name, onBlur, value } }) => (
              <FormControl>
                <FormLabel>From</FormLabel>
                <Input type='date' onChange={onChange} size='sm' onBlur={onBlur} value={value} name={name} />
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name='dateTo'
            render={({ field: { onChange, name, onBlur, value } }) => (
              <FormControl>
                <FormLabel>To</FormLabel>
                <Input type='date' onChange={onChange} size='sm' onBlur={onBlur} value={value} name={name} />
              </FormControl>
            )}
          />

          <HStack justify='flex-end'>
            <Button variant='outline' colorScheme='red' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='red' type='submit' disabled={handleDisableFilterButton()}>
              Filter
            </Button>
          </HStack>
        </Stack>
      }
    />
  );
};
